<template>
    <div class="h-full w-full flex flex-col bg-primary-light">

        <div ref="stage" class="flex-1 relative overflow-hidden">
            <iframe v-if="source" ref="frame" :key="source" :src="source"
                    :width="PAGE_WIDTH" :height="PAGE_HEIGHT" :style="frameStyle"
                    class="absolute top-0 left-0 border-0 origin-top-left"
                    @load="onLoad"></iframe>

            <div v-if="loading" class="absolute inset-0 bg-white/40 flex items-center justify-center">
                <ion-spinner class="size-12"></ion-spinner>
            </div>

            <div v-if="failed" class="absolute inset-0 bg-primary-light flex flex-col items-center justify-center px-6">
                <div class="bg-primary text-white font-bold px-6 py-4 rounded-3xl shadow-lg text-center max-w-screen-sm">
                    L'aperçu de ton livre n'a pas pu s'afficher.
                </div>
                <button-custom @click="reload" color="primary" shape="round" class="mt-6">Réessayer</button-custom>
            </div>
        </div>

        <div v-if="total > 1" class="shrink-0 bg-white px-6 pt-1 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <div class="flex items-center gap-4">
                <div @click="goTo(page - 1)" :class="{ 'invisible': page <= 1 }"
                     class="shrink-0 cursor-pointer rounded-full bg-primary size-12 flex items-center justify-center">
                    <ion-icon icon="assets/icon/arrow_right.svg" class="size-8 fill-white rotate-180"></ion-icon>
                </div>

                <input type="range" class="curseur-page flex-1" aria-label="Page du livre"
                       :min="1" :max="total" :value="page" :disabled="total < 2"
                       @input="onSlide" @pointerdown="dragging = true"
                       @pointerup="dragging = false" @pointercancel="dragging = false">

                <div @click="goTo(page + 1)" :class="{ 'invisible': page >= total }"
                     class="shrink-0 cursor-pointer rounded-full bg-primary size-12 flex items-center justify-center">
                    <ion-icon icon="assets/icon/arrow_right.svg" class="size-8 fill-white"></ion-icon>
                </div>
            </div>

            <div class="flex items-center justify-center gap-6 -mt-1">
                <div class="text-primary-dark font-bold">{{ page }} / {{ total }}</div>
            </div>
        </div>

        <ion-popover :event="outlineEvent" :is-open="outlineOpen" :dismiss-on-select="true"
                     alignment="center" @didDismiss="outlineOpen = false">
            <ion-list>
                <ion-item v-for="entry in outline" :key="entry.page" button :detail="false"
                          @click="goTo(entry.page)">
                    <ion-label class="font-bold text-primary-dark">{{ entry.label }}</ion-label>
                    <ion-note slot="end">p. {{ entry.page }}</ion-note>
                </ion-item>
            </ion-list>
        </ion-popover>

    </div>
</template>

<script>
import { IonIcon, IonItem, IonLabel, IonList, IonNote, IonPopover, IonSpinner } from '@ionic/vue';
import { defineComponent } from 'vue';
import { API_URL } from "@/constants/UrlConstant";
import ButtonCustom from "@/components/ButtonCustom.vue";

// Dimensions natives d'une page du livre. Le gabarit est dessiné en millimètres et en
// unités de fenêtre : il ne se redimensionne pas, il se met à l'échelle. On garde donc
// l'iframe à sa taille de conception et c'est un scale() qui l'ajuste à l'écran.
const PAGE_WIDTH = 1170;
const PAGE_HEIGHT = 810;

// Le script embarqué dans l'aperçu s'annonce dès l'analyse du document, donc avant
// l'évènement load. Passé ce délai après le load, c'est que la page n'est pas la nôtre
// (session expirée, erreur serveur).
const READY_TIMEOUT = 1500;

export default defineComponent({
    name: 'BookPreviewComponent',
    components: { IonIcon, IonItem, IonLabel, IonList, IonNote, IonPopover, IonSpinner, ButtonCustom },
    props: {
        bookId: [String, Number],
        houseId: [String, Number],
        // Numéro de page, ou type de page ('welcome', 'thanks') pour ouvrir l'aperçu
        // sur une page qu'on ne sait pas numéroter à l'avance.
        initialPage: [String, Number],
    },
    emits: ['ready'],
    data() {
        return {
            PAGE_WIDTH,
            PAGE_HEIGHT,
            page: 1,
            total: 1,
            outline: [],
            outlineOpen: false,
            outlineEvent: null,
            loading: true,
            failed: false,
            dragging: false,
            version: 0,
            scale: 1,
            offsetX: 0,
            offsetY: 0,
            observer: null,
            readyTimeout: null,
        };
    },
    computed: {
        source() {
            if (!this.bookId) {
                return null;
            }

            const house = this.houseId ?? this.$route.params.house;

            // Sans paramètre page, l'API renvoie le livre entier : une seule requête,
            // puis toute la navigation se fait dans le document déjà chargé.
            return API_URL + 'house/' + house + '/book/' + this.bookId + '/template'
                + '?token=' + localStorage.getItem('access_token')
                + '&v=' + this.version;
        },

        frameStyle() {
            return { transform: `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})` };
        },
    },
    mounted() {
        window.addEventListener('message', this.onMessage);
        window.addEventListener('keydown', this.onKeydown);

        this.observer = new ResizeObserver(this.fit);
        this.observer.observe(this.$refs.stage);
        this.fit();
    },
    unmounted() {
        window.removeEventListener('message', this.onMessage);
        window.removeEventListener('keydown', this.onKeydown);
        this.observer?.disconnect();
        clearTimeout(this.readyTimeout);
    },
    watch: {
        source() {
            // Le compte à rebours de l'aperçu précédent ne doit pas déclarer celui-ci
            // en échec alors qu'il vient à peine d'être demandé.
            clearTimeout(this.readyTimeout);
            this.loading = true;
            this.failed = false;
        },
    },
    methods: {
        /**
         * Recharge l'aperçu, par exemple après une modification de la personnalisation.
         */
        reload() {
            this.version++;
        },

        /**
         * Ouvre le sommaire, ancré sur le bouton qui l'ouvre — celui-ci vit hors de
         * l'aperçu (barre de navigation du livre, en-tête de la modale).
         */
        openOutline(event) {
            // Le popover se cale sur la cible de l'évènement. Telle quelle, ce serait
            // l'icône ou l'ondulation touchée à l'intérieur du bouton : on remonte donc
            // au bouton entier, seul élément dont la géométrie aligne le sommaire dessous.
            const anchor = event.currentTarget ?? event.target?.closest?.('a, ion-button, button') ?? event.target;
            const box = anchor.getBoundingClientRect();
            const origin = this.fixedOrigin();

            // Ionic ne lit de la cible que sa boîte : on la lui donne déjà ramenée dans
            // son repère. Un objet suffit, et il survit à la fin de la propagation de
            // l'évènement, contrairement à currentTarget.
            this.outlineEvent = {
                target: {
                    getBoundingClientRect: () => ({
                        top: box.top - origin.top,
                        left: box.left - origin.left,
                        width: box.width,
                        height: box.height,
                    }),
                },
            };
            this.outlineOpen = true;
        },

        /**
         * Coin haut-gauche du bloc dans lequel le popover se positionne.
         *
         * Ionic place le popover en position fixe et calcule sa cible en coordonnées de
         * fenêtre. Mais l'application est encadrée (body en largeur fixe, centré) et Ionic
         * pose `contain: layout` sur ion-app et sur les pages : ces éléments deviennent le
         * bloc conteneur des positions fixes, qui ne commence donc pas au bord de la
         * fenêtre. Sans cette mesure, le sommaire s'affiche décalé de la marge du gabarit.
         */
        fixedOrigin() {
            const probe = document.createElement('div');
            probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden';

            this.$el.appendChild(probe);
            const { top, left } = probe.getBoundingClientRect();
            probe.remove();

            return { top, left };
        },

        fit() {
            const stage = this.$refs.stage;
            if (!stage) {
                return;
            }

            const { width, height } = stage.getBoundingClientRect();

            // La page reste entière et centrée : jamais rognée, jamais déformée.
            this.scale = Math.min(width / PAGE_WIDTH, height / PAGE_HEIGHT);
            this.offsetX = Math.round((width - PAGE_WIDTH * this.scale) / 2);
            this.offsetY = Math.round((height - PAGE_HEIGHT * this.scale) / 2);
        },

        goTo(page, smooth = true) {
            const frame = this.$refs.frame;
            if (!frame) {
                return;
            }

            // Une cible non numérique est un type de page : l'aperçu sait la retrouver,
            // il n'y a rien à borner.
            if (!isNaN(page) && (page < 1 || page > this.total)) {
                return;
            }

            frame.contentWindow?.postMessage(
                { target: 'book-preview', type: 'goto', page, smooth },
                new URL(API_URL).origin
            );
        },

        onSlide(event) {
            const page = Number(event.target.value);

            // Le compteur suit le curseur sans attendre le retour de l'aperçu : la valeur
            // liée reste ainsi celle du bouton qu'on déplace, et Vue ne le repositionne
            // pas en plein glissement.
            this.page = page;

            // Défilement immédiat : une animation par cran ferait traîner l'aperçu
            // derrière le doigt.
            this.goTo(page, false);
        },

        onLoad() {
            clearTimeout(this.readyTimeout);
            this.readyTimeout = setTimeout(() => {
                if (this.loading) {
                    this.failed = true;
                    this.loading = false;
                }
            }, READY_TIMEOUT);
        },

        onMessage(event) {
            if (event.data?.source !== 'book-preview' || event.source !== this.$refs.frame?.contentWindow) {
                return;
            }

            if (event.data.type === 'ready') {
                clearTimeout(this.readyTimeout);
                this.total = event.data.total;
                this.outline = event.data.outline ?? [];
                this.loading = false;
                this.failed = false;
                this.$emit('ready', { total: this.total, outline: this.outline });

                if (this.initialPage) {
                    this.goTo(this.initialPage, false);
                }
                return;
            }

            // Le curseur pilote déjà le défilement pendant le glissement : le laisser
            // suivre en plus les pages traversées le ferait sauter sous le doigt.
            if (event.data.type === 'page' && !this.dragging) {
                this.page = event.data.page;
            }
        },

        onKeydown(event) {
            // Les flèches appartiennent au champ de saisie quand il y en a un au premier plan.
            if (event.target?.closest('input, textarea, [contenteditable]')) {
                return;
            }

            if (event.key === 'ArrowRight' || event.key === 'PageDown') {
                this.goTo(this.page + 1);
            }
            else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
                this.goTo(this.page - 1);
            }
        },
    },
});
</script>

<style scoped>
/* Le sommaire liste des titres d'activités : la largeur par défaut d'un popover les
   couperait en deux ou trois lignes. */
ion-popover {
    --width: 380px;
    --max-width: 90vw;
}

/* Curseur natif plutôt qu'ion-range : à chaque changement de page, ce dernier mesurait sa
   propre géométrie pour repositionner son bouton. Cette lecture de mise en page, synchrone
   et dans le document parent, bloquait le rendu le temps d'une image — et l'aperçu, que ce
   même parent composite, sursautait à chaque franchissement de page. */
.curseur-page {
    -webkit-appearance: none;
    appearance: none;
    height: 24px;
    background: transparent;
    cursor: pointer;
}

.curseur-page:disabled {
    opacity: .4;
    cursor: default;
}

.curseur-page::-webkit-slider-runnable-track {
    height: 6px;
    border-radius: 999px;
    background: var(--ion-color-primary-tint);
}

.curseur-page::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    /* Centre le bouton sur la piste : (hauteur piste - hauteur bouton) / 2. */
    margin-top: -8px;
    border-radius: 50%;
    background: var(--ion-color-primary);
    box-shadow: 0 1px 3px rgba(0, 0, 0, .3);
}

.curseur-page::-moz-range-track {
    height: 6px;
    border-radius: 999px;
    background: var(--ion-color-primary-tint);
}

.curseur-page::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 50%;
    background: var(--ion-color-primary);
    box-shadow: 0 1px 3px rgba(0, 0, 0, .3);
}
</style>
